import React, { useRef } from 'react';
import { Box, Chip, Paper, Typography, IconButton, Tooltip } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { colors } from '@src/styles';

const SyntaxHighlightedJson: React.FC<{ jsonString: string }> = ({ jsonString }) => {
  const highlightJson = (str: string) => {
    const elements: React.ReactNode[] = [];

    const patterns = [
      { regex: /"([^"]*)":/g, type: 'key' }, // Keys (property names)
      { regex: /:\s*"([^"]*)"/g, type: 'string' }, // String values
      { regex: /:\s*(true|false|null)/g, type: 'keyword' }, // Keywords
      { regex: /:\s*(-?\d+\.?\d*)/g, type: 'number' }, // Numbers
      { regex: /([{}\[\],])/g, type: 'punctuation' }, // Brackets and punctuation
    ];

    const colorMap = {
      key: '#4EC9B0', // Teal for keys
      string: '#CE9178', // Orange for strings
      keyword: '#569CD6', // Blue for keywords
      number: '#B5CEA8', // Green for numbers
      punctuation: '#FFD700', // Gold for brackets/punctuation
      default: '#E0E0E0', // Default text color
    };

    const lines = str.split('\n');

    lines.forEach((line, lineIndex) => {
      let lineElements: React.ReactNode[] = [];
      let lastIndex = 0;
      const matches: Array<{ index: number; length: number; type: string; text: string }> = [];

      patterns.forEach(({ regex, type }) => {
        const lineRegex = new RegExp(regex.source, regex.flags);
        let match;
        while ((match = lineRegex.exec(line)) !== null) {
          matches.push({
            index: match.index,
            length: match[0].length,
            type,
            text: match[0],
          });
        }
      });

      matches.sort((a, b) => a.index - b.index);

      const filteredMatches: typeof matches = [];
      matches.forEach((match) => {
        const overlaps = filteredMatches.some(
          (existing) => match.index < existing.index + existing.length && match.index + match.length > existing.index,
        );
        if (!overlaps) {
          filteredMatches.push(match);
        }
      });

      filteredMatches.forEach((match, i) => {
        if (match.index > lastIndex) {
          lineElements.push(
            <span key={`${lineIndex}-text-${i}`} style={{ color: colorMap.default }}>
              {line.substring(lastIndex, match.index)}
            </span>,
          );
        }

        lineElements.push(
          <span key={`${lineIndex}-match-${i}`} style={{ color: colorMap[match.type as keyof typeof colorMap] }}>
            {match.text}
          </span>,
        );

        lastIndex = match.index + match.length;
      });

      if (lastIndex < line.length) {
        lineElements.push(
          <span key={`${lineIndex}-text-end`} style={{ color: colorMap.default }}>
            {line.substring(lastIndex)}
          </span>,
        );
      }

      if (lineElements.length === 0) {
        lineElements.push(
          <span key={`${lineIndex}-empty`} style={{ color: colorMap.default }}>
            {line}
          </span>,
        );
      }

      elements.push(
        <React.Fragment key={`line-${lineIndex}`}>
          {lineElements}
          {lineIndex < lines.length - 1 && '\n'}
        </React.Fragment>,
      );
    });

    return elements;
  };

  return <>{highlightJson(jsonString)}</>;
};

interface RawJsonEditorProps {
  value: string;
  onChange?: (value: string) => void;
  errors?: string[];
  minHeight?: string;
}

interface ParsedError {
  line?: number;
  column?: number;
  message: string;
  path?: string;
}

const parseAjvError = (errorString: string): ParsedError => {
  try {
    const parsed = JSON.parse(errorString);
    return {
      message: parsed.message || errorString,
      path: parsed.instancePath || parsed.dataPath,
    };
  } catch {
    // Try to extract line/column from syntax errors
    const lineMatch = errorString.match(/line (\d+)/i);
    const columnMatch = errorString.match(/column (\d+)/i);

    return {
      line: lineMatch ? parseInt(lineMatch[1]) : undefined,
      column: columnMatch ? parseInt(columnMatch[1]) : undefined,
      message: errorString,
    };
  }
};

const getErrorLines = (value: string, errors: string[]): Set<number> => {
  const errorLines = new Set<number>();

  errors.forEach((errorString) => {
    const error = parseAjvError(errorString);

    if (error.line !== undefined) {
      errorLines.add(error.line - 1); // Convert to 0-based
    } else if (error.path) {
      // Try to find the line for the error path
      const lines = value.split('\n');
      const pathParts = error.path.split('/').filter(Boolean);

      lines.forEach((line, index) => {
        pathParts.forEach((part) => {
          if (line.includes(`"${part}":`)) {
            errorLines.add(index);
          }
        });
      });
    }
  });

  return errorLines;
};

const RawJsonEditor: React.FC<RawJsonEditorProps> = ({ value, onChange, errors, minHeight }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  const errorLines = errors && errors.length > 0 ? getErrorLines(value, errors) : new Set<number>();

  const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (highlightRef.current && e.currentTarget) {
      highlightRef.current.scrollTop = e.currentTarget.scrollTop;
      highlightRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
    if (lineNumbersRef.current && e.currentTarget) {
      lineNumbersRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  const handleFormatJson = () => {
    if (!onChange) return;

    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      onChange(formatted);
    } catch (error) {
      // If JSON is invalid, don't format
      console.error('Cannot format invalid JSON:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', minHeight: minHeight || '300px' }}>
      <Box
        sx={{
          flex: 1,
          background: colors.background.primary,
          borderRadius: '8px',
          border: `2px solid ${errors?.length > 0 ? '#FC2121' : colors.background.secondary}`,
          padding: '16px',
          paddingLeft: '60px',
          position: 'relative',
          transition: 'all 0.3s ease',
          boxShadow: errors?.length > 0 ? '0 4px 20px rgba(252, 33, 33, 0.15)' : '0 4px 20px rgba(0, 0, 0, 0.3)',
          '&:focus-within': {
            borderColor: errors?.length > 0 ? '#FC2121' : colors.primary.yellow,
            boxShadow: `0 0 0 3px ${errors?.length > 0 ? 'rgba(252, 33, 33, 0.1)' : 'rgba(255, 193, 7, 0.1)'}, 0 8px 30px rgba(255, 193, 7, 0.2)`,
            transform: 'translateY(-2px)',
          },
        }}
      >
        {/* Line numbers with error indicators */}
        <Box
          ref={lineNumbersRef}
          sx={{
            position: 'absolute',
            top: '16px',
            left: '0px',
            bottom: '16px',
            width: '50px',
            overflow: 'hidden',
            borderRight: `1px solid ${colors.background.secondary}`,
            fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", "Monaco", monospace',
            fontSize: '14px',
            lineHeight: '1.8',
            color: colors.primary.white,
            opacity: 0.4,
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          {value.split('\n').map((_, index) => (
            <Box
              key={index}
              sx={{
                paddingRight: '8px',
                textAlign: 'right',
                height: '25.2px', // lineHeight * fontSize
                backgroundColor: errorLines.has(index) ? 'rgba(252, 33, 33, 0.15)' : 'transparent',
                color: errorLines.has(index) ? '#FC2121' : 'inherit',
                fontWeight: errorLines.has(index) ? 700 : 400,
                opacity: errorLines.has(index) ? 1 : 0.4,
                borderLeft: errorLines.has(index) ? '3px solid #FC2121' : '3px solid transparent',
                transition: 'all 0.2s ease',
              }}
            >
              {index + 1}
            </Box>
          ))}
        </Box>

        {/* Syntax highlighted background layer */}
        <Box
          ref={highlightRef}
          sx={{
            position: 'absolute',
            top: '16px',
            left: '60px',
            right: '16px',
            bottom: '16px',
            pointerEvents: 'none',
            overflow: 'hidden',
            fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", "Monaco", monospace',
            fontSize: '14px',
            lineHeight: '1.8',
            letterSpacing: '0.5px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            userSelect: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <SyntaxHighlightedJson jsonString={value} />
        </Box>

        {/* Transparent textarea overlay */}
        <Box
          ref={textareaRef}
          component="textarea"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={handleScroll}
          spellCheck={false}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
            border: 'none',
            color: 'transparent',
            fontSize: '14px',
            fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", "Monaco", monospace',
            lineHeight: '1.8',
            resize: 'none',
            outline: 'none',
            tabSize: 2,
            letterSpacing: '0.5px',
            caretColor: colors.primary.yellow,
            transition: 'all 0.2s ease',
            WebkitTextFillColor: 'transparent',
            '&::selection': {
              backgroundColor: 'rgba(255, 193, 7, 0.3)',
              color: 'transparent',
            },
            '&::placeholder': {
              color: colors.primary.white,
              opacity: 0.3,
            },
            '&::-webkit-scrollbar': {
              width: '12px',
              height: '12px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '6px',
              border: '2px solid transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: `linear-gradient(135deg, ${colors.primary.yellow} 0%, #FFA500 100%)`,
              borderRadius: '6px',
              border: '2px solid transparent',
              backgroundClip: 'padding-box',
              boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
              '&:hover': {
                background: `linear-gradient(135deg, ${colors.primary.white} 0%, ${colors.primary.yellow} 100%)`,
              },
              '&:active': {
                background: colors.primary.yellow,
              },
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: 'transparent',
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {onChange && (
            <Tooltip title="Formatar JSON" arrow placement="left">
              <IconButton
                onClick={handleFormatJson}
                size="small"
                sx={{
                  backgroundColor: colors.primary.yellow,
                  color: colors.background.primary,
                  width: '32px',
                  height: '32px',
                  boxShadow: '0 2px 8px rgba(255, 193, 7, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: colors.primary.yellow,
                    transform: 'scale(1.1) rotate(15deg)',
                    boxShadow: '0 4px 12px rgba(255, 193, 7, 0.5)',
                  },
                  '&:active': {
                    transform: 'scale(0.95) rotate(0deg)',
                  },
                }}
              >
                <AutoFixHighIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          )}
          {errors?.length === 0 && value && (
            <Chip
              label="✓ Valid JSON"
              size="small"
              sx={{
                backgroundColor: colors.primary.green,
                color: colors.background.primary,
                fontSize: '0.7rem',
                height: '24px',
                fontWeight: 700,
                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.5)',
                },
              }}
            />
          )}
        </Box>
      </Box>
      {errors?.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            padding: '16px',
            backgroundColor: 'rgba(252, 33, 33, 0.08)',
            border: '2px solid #FC2121',
            borderRadius: '8px',
            maxHeight: '150px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(252, 33, 33, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#FC2121',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#ff4444',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box
              sx={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#FC2121',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                color: colors.primary.white,
              }}
            >
              !
            </Box>
            <Typography variant="subtitle2" sx={{ color: '#FC2121', fontWeight: 700, fontSize: '0.85rem' }}>
              Erros encontrados ({errors?.length})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {errors?.map((error, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  padding: '8px',
                  backgroundColor: 'rgba(252, 33, 33, 0.05)',
                  borderRadius: '4px',
                  borderLeft: '3px solid #FC2121',
                }}
              >
                <Typography
                  sx={{
                    color: '#FC2121',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    lineHeight: '1.5',
                    flex: 1,
                  }}
                >
                  {error}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default RawJsonEditor;
